import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, MailCheck, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const VERIFY_CHIP = {
  verified: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
};

export const OwnerLeadsPanel = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/owner-leads").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: rows.length,
    verified: rows.filter(r => r.verification_status === "verified").length,
    pending: rows.filter(r => r.verification_status !== "verified").length,
  }), [rows]);

  const filtered = useMemo(() => {
    if (filter === "verified") return rows.filter(r => r.verification_status === "verified");
    if (filter === "pending") return rows.filter(r => r.verification_status !== "verified");
    return rows;
  }, [rows, filter]);

  const markContacted = async (id) => {
    await api.put(`/owner-leads/${id}`, { status: "contacted" });
    toast.success("Marked contacted");
    load();
  };

  return (
    <div className="space-y-4" data-testid="owner-leads-panel">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><Home className="w-3.5 h-3.5" /> Owner leads</div>
        <h2 className="text-lg font-bold">Property owner leads</h2>
        <p className="text-sm text-slate-500 mt-1">From /provider. A lead is <strong>unverified</strong> until the owner clicks the confirmation link we emailed them. Reply only to verified leads.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="owner-leads-filter-bar">
        {[{ k: "all", label: "All" }, { k: "verified", label: "Verified" }, { k: "pending", label: "Unverified" }].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`owner-filter-${f.k}`}
            className={`px-3 py-1.5 text-xs rounded-full border ${filter === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>
            {f.label} <span className="ml-1 opacity-70">{counts[f.k]}</span>
          </button>
        ))}
      </div>
      <Card className="p-0 border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 italic">No owner leads in this view.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Received</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Property</th>
                <th className="text-left px-3 py-2">Interest</th>
                <th className="text-left px-3 py-2">Email verification</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const verified = r.verification_status === "verified";
                const p = r.property || {};
                return (
                  <tr key={r.id} className={`border-b border-slate-100 ${verified ? "" : "bg-amber-50/30"}`} data-testid={`owner-lead-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{(r.created_at || "").slice(0,10)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold"><Link to={`/leads/${r.id}`} className="hover:underline" data-testid={`owner-lead-profile-${r.id}`}>{r.name}</Link></div>
                      <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{p.address || "—"}</div>
                      {p.bedrooms != null && <div className="text-slate-500">{p.bedrooms}BR / {p.bathrooms}BA / {p.sqft} sqft{p.occupied ? " · occupied" : ""}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.clicked_use_type || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${VERIFY_CHIP[verified ? "verified" : "pending"]}`} data-testid={`owner-lead-verify-${r.id}`}>
                        {verified ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{verified ? "verified" : "unverified"}
                      </span>
                      {verified && r.verified_at && <div className="text-[10px] text-slate-500 mt-0.5">{r.verified_at.slice(0,16).replace("T"," ")} UTC</div>}
                      {!verified && r.verification_send_error && <div className="text-[10px] text-red-600 mt-0.5">confirm email failed</div>}
                    </td>
                    <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{r.status || "new"}</span></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {verified && r.status !== "contacted" && (
                        <Button size="sm" variant="outline" onClick={() => markContacted(r.id)} data-testid={`owner-lead-contacted-${r.id}`}>
                          <MailCheck className="w-3.5 h-3.5 mr-1" /> Contacted
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};
